"use client";

import { Box, Card, Flex, Text } from "@radix-ui/themes";
import { Badge, Button, Skeleton } from "./elements.js";
import {
  getComparativeReadableDate,
  getDomProps,
  getUserLocation,
  parseUserAgent,
  type WidgetRootDomProps,
  type WidgetRootState,
} from "./utils.js";
import { LogoutDialog } from "./logout-dialog.js";
import { LogoutAllSessionsDialog } from "./logout-all-sessions-dialog.js";
import { ActiveSession } from "../api/endpoint.js";
import * as CardList from "./card-list.js";
import { IconPanel } from "./icon-panel.js";
import { useState } from "react";
import { GenericError } from "./generic-error.js";
import { LaptopIcon, MobileIcon } from "@radix-ui/react-icons";
import { Translation } from "./i18n/translation.js";
import { useLocale } from "./i18n/use-locale.js";

interface UserSessionsProps extends WidgetRootDomProps {
  sessionsData: ActiveSession[];
  currentSessionId: string;
}

const UserSessions = ({
  sessionsData: sessions,
  currentSessionId,
  ...domProps
}: UserSessionsProps) => {
  const currentSession = sessions.find(
    (session) => session.id === currentSessionId,
  );
  const otherSessions = sessions.filter(
    (session) => session.id !== currentSessionId,
  );
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [sessionToSignOut, setSessionToSignOut] =
    useState<ActiveSession | null>(null);
  const [openLogoutAllSessionsDialog, setOpenLogoutAllSessionsDialog] =
    useState(false);

  return (
    <Flex
      direction="column"
      gap="4"
      {...getWidgetRootDomProps("resolved", domProps)}
    >
      <CardList.Root>
        {currentSession && (
          <CardList.Item>
            <UserSession session={currentSession} isCurrentSession />
          </CardList.Item>
        )}

        {otherSessions.map((session) => (
          <CardList.Item key={session.id}>
            <UserSession
              session={session}
              onSignOut={() => {
                setSessionToSignOut(session);
                setOpenLogoutDialog(true);
              }}
            />
          </CardList.Item>
        ))}
      </CardList.Root>

      {sessions.length > 1 && (
        <Card size="2">
          <Flex gap="2" justify="between" align="center">
            <Box>
              <Text size="2" highContrast weight="bold" as="p">
                <Translation
                  defaultMessage="Sign out of all other devices"
                  id="sfkEFa"
                  description="Heading for sign out all other devices action"
                />
              </Text>
              <Text size="2" color="gray" as="p">
                <Translation
                  defaultMessage="Remove access from all devices except this one"
                  id="LsD5BK"
                  description="Description for sign out all other devices action"
                />
              </Text>
            </Box>
            <Button
              variant="secondary"
              onClick={() => {
                setOpenLogoutAllSessionsDialog(true);
              }}
            >
              <Translation
                defaultMessage="Sign out"
                id="oKQ0rm"
                description="Button text to sign out of all other devices"
              />
            </Button>
          </Flex>
        </Card>
      )}

      {sessionToSignOut && (
        <LogoutDialog
          session={sessionToSignOut}
          open={openLogoutDialog}
          onOpenChange={setOpenLogoutDialog}
        />
      )}

      <LogoutAllSessionsDialog
        currentSessionId={currentSessionId}
        open={openLogoutAllSessionsDialog}
        onOpenChange={setOpenLogoutAllSessionsDialog}
      />
    </Flex>
  );
};

interface UserSessionsLoadingProps extends WidgetRootDomProps {}

const UserSessionsLoading: React.FC<UserSessionsLoadingProps> = (props) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("loading", props)}>
      <Flex gap="4" align="center">
        <Skeleton>
          <IconPanel />
        </Skeleton>

        <Flex direction="column">
          <Text size="2" highContrast weight="bold" as="p" mb="-2px">
            <Skeleton>
              <Translation
                defaultMessage="The location"
                id="+7UnNu"
                description="Loading placeholder text for session location"
              />
            </Skeleton>
          </Text>

          <Text size="2" color="gray" as="p">
            <Skeleton>
              <Translation
                defaultMessage="The location"
                id="+7UnNu"
                description="Loading placeholder text for session location"
              />
            </Skeleton>
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
};

interface UserSessionsErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const UserSessionsError: React.FC<UserSessionsErrorProps> = ({
  error,
  ...domProps
}) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <GenericError error={error} />
    </Card>
  );
};

interface UserSessionProps {
  session: ActiveSession;
  isCurrentSession?: boolean;
  onSignOut?: (session: ActiveSession) => void;
}

const UserSession = ({
  session,
  isCurrentSession = false,
  onSignOut,
}: UserSessionProps) => {
  const userAgent = parseUserAgent(session.userAgent);
  const userLocation = getUserLocation(
    session.currentLocation,
    session.ipAddress,
  );
  const locale = useLocale();

  return (
    <Flex gap="4" align="center">
      <IconPanel>
        {userAgent.isMobile ? <MobileIcon /> : <LaptopIcon />}
      </IconPanel>

      <Flex direction="column">
        <Text size="2" highContrast weight="bold" as="p" mb="-2px">
          {userAgent.pretty}
        </Text>

        <Flex gap="1" align="center">
          <Text size="2" color="gray" as="p">
            {userLocation}
          </Text>

          {isCurrentSession && (
            <Badge ml="2">
              <Translation
                defaultMessage="This device"
                id="Wz8T8u"
                description="Badge label indicating the current device session"
              />
            </Badge>
          )}

          {!isCurrentSession && session.lastActivityAt && (
            <>
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              <Text size="2" color="gray">
                ∙
              </Text>
              <Text size="2" color="gray">
                <Translation
                  defaultMessage="Last seen {timeAgo}"
                  id="gF+kUw"
                  description="Text showing when the session was last active"
                  values={{
                    timeAgo: getComparativeReadableDate(
                      new Date(),
                      new Date(session.lastActivityAt),
                      { locale },
                    ),
                  }}
                />
              </Text>
            </>
          )}
        </Flex>
      </Flex>

      {!isCurrentSession && (
        <Flex ml="auto">
          <Button variant="secondary" onClick={() => onSignOut?.(session)}>
            <Translation
              defaultMessage="Sign out"
              id="sT1L78"
              description="Button text to sign out of a session"
            />
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "user-sessions",
    widgetState: state,
  });
}

export type { UserSessionsLoadingProps, UserSessionsErrorProps };
export { UserSessions, UserSessionsLoading, UserSessionsError };
